import { faShoppingCart } from '@fortawesome/free-solid-svg-icons'
import { graphql } from 'gatsby'
import gsap from 'gsap'
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'

import { MainMenuFragmentFragment } from '../../../../graphql/gatsby'
import useIsMounted from '../../../../hooks/useIsMounted'
import useSiteSettings from '../../../../hooks/useSiteSettings'
import { publishViewCartEvent } from '../../../../lib/analytics/cart'
import { useCartContext } from '../../../../lib/cart'
import { ToggleCartContext } from '../../../../lib/cart/context'
import { totalQuantityExcludingDependents } from '../../../../lib/cart/item'
import { mainMenuTop$ } from '../../../../lib/header/parallax'
import {
  UseCanonicalLocation,
  useCanonicalLocation,
} from '../../../../lib/pages/hooks/useCanonicalLocation'
import { useSanityRoleAndContextBasedContent } from '../../../../lib/sanity'
import { normalizeSanityLink } from '../../../../lib/sanity/link'
import { filterMenuEntriesToDisplay } from '../../../../utils/sanity/documents/menuEntry'
import { StyledFunctionComponent } from '../../../types'
import SiteLogo from '../../SiteLogo'
import { MainMenuEntry } from '../MainMenuEntry'
import { SearchContainer } from '../SearchContainer'
import SearchInput from '../SearchInput'
import * as Styled from './styled'

interface DesktopMainMenuEntryProps {
  canonicalLocation: UseCanonicalLocation
  entry: NonNullable<NonNullable<MainMenuFragmentFragment['mainMenu']>[number]>
  isTempActive: boolean
  onBlurOrLeave: () => void
  onFocusOrEnter: () => void
}

const DesktopMainMenuEntry: React.VFC<DesktopMainMenuEntryProps> = ({
  canonicalLocation,
  entry,
  isTempActive,
  onBlurOrLeave,
  onFocusOrEnter,
}) => {
  // set isActive if current URL begins with menu entry's path
  const isActive = useMemo(() => {
    if (isTempActive) {
      return true
    }
    const { link } = entry
    const normalized = link ? normalizeSanityLink({ link }) : undefined
    const normalizedHref = normalized?.href
    if (!normalizedHref) {
      return false
    }
    const startsWithRegex = new RegExp(`^${normalizedHref}(?:[/?].*)?$`)
    return startsWithRegex.test(canonicalLocation.path)
  }, [canonicalLocation.path, entry, isTempActive])

  return (
    <Styled.ActivatingNavItem
      $isActive={isActive}
      onFocus={onFocusOrEnter}
      onMouseEnter={onFocusOrEnter}
      onBlur={onBlurOrLeave}
      onMouseLeave={onBlurOrLeave}
    >
      <MainMenuEntry menuEntry={entry} isTempActive={isTempActive} />
    </Styled.ActivatingNavItem>
  )
}

const DesktopMainMenu: StyledFunctionComponent = ({ className }) => {
  const { cart } = useCartContext()
  const { isCartOpen, toggleCart: toggleCartModal } = useContext(ToggleCartContext)
  const [inputValue, setInputValue] = useState('')
  const [isInputActive, setIsInputActive] = useState(false)

  const { mainMenu: menuEntriesUnfiltered } = useSiteSettings() || {}
  const { shouldDisplayContent } = useSanityRoleAndContextBasedContent()
  const menuEntries = useMemo(
    () => filterMenuEntriesToDisplay(shouldDisplayContent)(menuEntriesUnfiltered),
    [menuEntriesUnfiltered, shouldDisplayContent],
  )

  const [tempActiveEntry, setTempActiveEntryState] = useState<string | null | undefined>(null)
  const canonicalLocation = useCanonicalLocation()

  const mainNavListRef = useRef<HTMLUListElement>(null)
  const tempActiveTimeoutRef = useRef<number | null>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)
  const isMounted = useIsMounted()

  const resetTempActiveTimeout = useCallback(() => {
    if (tempActiveTimeoutRef.current) {
      clearTimeout(tempActiveTimeoutRef.current)
      tempActiveTimeoutRef.current = null
    }
  }, [])

  const handleCartButtonClick = () => {
    toggleCartModal()
    if (cart) {
      publishViewCartEvent(cart)
    }
  }

  const setTempActiveEntry = useCallback(
    (entry: string | null | undefined) => {
      if (entry) {
        resetTempActiveTimeout()
      }
      // only set state if component mounted to prevent React error
      if (isMounted.current) {
        setTempActiveEntryState(entry)
      }
    },
    [isMounted, resetTempActiveTimeout],
  )

  const clearTempActiveEntry = useCallback(() => {
    if (!isMounted.current) {
      return
    }
    setTempActiveEntry(null)
    setIsInputActive(false)
  }, [isMounted, setTempActiveEntry, setIsInputActive])

  const setTempActiveTimeout = useCallback(() => {
    if (isInputActive) {
      return
    }
    tempActiveTimeoutRef.current =
      typeof window !== 'undefined' ? window.setTimeout(clearTempActiveEntry, 350) : null
  }, [clearTempActiveEntry, isInputActive])

  useEffect(() => {
    const subscription = mainMenuTop$.subscribe((mainMenuTop) => {
      if (mainNavListRef.current) {
        gsap.set(mainNavListRef.current, { top: mainMenuTop })
      }
    })
    return () => subscription.unsubscribe()
  }, [])

  useEffect(() => {
    if (isInputActive) {
      setTempActiveEntry('Search')
    } else if (tempActiveEntry === 'Search') {
      setTempActiveEntry(null)
    }
  }, [isInputActive, tempActiveEntry, setTempActiveEntry])

  const menuEntryElements = useMemo(
    () =>
      menuEntries.reduce((acc: React.ReactElement[], entry) => {
        if (!entry) return acc
        acc.push(
          <DesktopMainMenuEntry
            key={entry._id}
            canonicalLocation={canonicalLocation}
            entry={entry}
            isTempActive={tempActiveEntry === entry.name}
            onBlurOrLeave={setTempActiveTimeout}
            onFocusOrEnter={() => setTempActiveEntry(entry.name)}
          />,
        )
        return acc
      }, []),
    [canonicalLocation, menuEntries, setTempActiveEntry, setTempActiveTimeout, tempActiveEntry],
  )
  const searchNavElement = useMemo(
    () => (
      <Styled.ActivatingNavItem $isActive={false} style={{ cursor: 'unset' }}>
        <SearchInput
          inputRef={searchInputRef}
          inputValue={inputValue}
          setInputValue={setInputValue}
          closeSearch={clearTempActiveEntry}
          isActive={isInputActive}
          setIsActive={setIsInputActive}
        />
        <SearchContainer
          closeSearch={clearTempActiveEntry}
          inputValue={inputValue}
          showSearch={isInputActive}
        />
      </Styled.ActivatingNavItem>
    ),
    [clearTempActiveEntry, inputValue, isInputActive],
  )

  const totalQuantity = cart && totalQuantityExcludingDependents(cart.items, cart.total_quantity)
  const cartQuantityText = totalQuantity ? `${totalQuantity < 100 ? totalQuantity : '99+'}` : ''

  return (
    <nav aria-label="Main Navigation" className={className} data-cy="main-nav">
      <Styled.GlobalNav ref={mainNavListRef}>
        <Styled.LogoNavItem>
          <Styled.LogoLink to="/">
            <SiteLogo
              className="logo"
              alt="Home"
              dfhLogoComponent={Styled.DfhLogo}
              dispensaryLogoComponent={Styled.DispensaryLogo}
            />
          </Styled.LogoLink>
        </Styled.LogoNavItem>
        {menuEntryElements}
        {searchNavElement}
        <Styled.CartActivatingNavItem $isActive={isCartOpen}>
          <Styled.CartLink onClick={handleCartButtonClick} data-cy="cart-item-button">
            <Styled.ExtraLargeOnlyText>Cart</Styled.ExtraLargeOnlyText>
            <Styled.NavItemIcon icon={faShoppingCart} />
            <Styled.CartItemCount aria-hidden data-cy="cart-item-count">
              {cartQuantityText}
            </Styled.CartItemCount>
          </Styled.CartLink>
        </Styled.CartActivatingNavItem>
        <Styled.ShopNowNavItem>
          <Styled.ShopNowButton to="/products">Shop Now</Styled.ShopNowButton>
        </Styled.ShopNowNavItem>
      </Styled.GlobalNav>
    </nav>
  )
}

const StyledDesktopMainMenu = styled(DesktopMainMenu)`
  position: relative;
  top: 0;
  width: 100%;
  max-width: 970px;
  margin: 0 auto;

  @media (min-width: 992px) {
    display: block;
  }
  @media (min-width: 1200px) {
    max-width: 1200px;
  }
`

export { StyledDesktopMainMenu as DesktopMainMenu }

export const mainMenu = graphql`
  fragment mainMenuFragment on SanitySiteSettings {
    mainMenu {
      ...MenuEntryFragment
      childEntries {
        ...MenuEntryFragment
        childEntries {
          ...MenuEntryFragment
          childEntries {
            ...MenuEntryFragment
          }
        }
      }
    }
  }
`
